/* BDF PARTICLE LOGO — canonical single-canvas controller. */
(() => {
  'use strict';

  const IMAGE_URL='https://cdn.shopify.com/s/files/1/0632/7717/0912/files/icone_particle_effect.png?v=1788033013';
  const MAIN_SELECTOR='main.main-content';
  const HOME_CAROUSEL_SELECTOR='[data-bdf-home-3d-carousel]';
  const HOME_STAGE_SELECTOR='.bdf-home-3d-carousel__stage';
  const HOME_MOBILE_SELECTOR='.bdf-home-3d-carousel__mobile';
  const MOBILE_QUERY=matchMedia('(max-width:768px)');
  const REDUCED_QUERY=matchMedia('(prefers-reduced-motion:reduce)');
  const TAU=Math.PI*2;
  const MAX_PARTICLES=1152;
  const MOBILE_PARTICLES=500;
  const DESKTOP_PARTICLES=1152;
  const FRAME_MS=1000/60;
  const IMPACT_MS=1380;
  const RECOVER_MS=620;
  const FLASH_MS=620;
  const DEPTH_BUCKETS=12;
  const VERSION='3.0.0';

  let controller=null;
  let transientController=null;
  let bootPromise=null;
  let maskPromise=null;

  const clamp=(value,min,max)=>Math.max(min,Math.min(max,value));
  const lerp=(a,b,amount)=>a+(b-a)*amount;
  const fract=value=>value-Math.floor(value);
  const smoothstep=(edge0,edge1,value)=>{
    const x=clamp((value-edge0)/(edge1-edge0),0,1);
    return x*x*(3-2*x);
  };
  const circularGaussian=(value,center,width)=>{
    const distance=Math.abs(fract(value)-fract(center));
    const delta=Math.min(distance,1-distance)/width;
    return Math.exp(-delta*delta);
  };
  const rgba=(color,alpha)=>`rgba(${color[0]},${color[1]},${color[2]},${clamp(alpha,0,1).toFixed(3)})`;

  const colorStops=[
    [34,112,255],
    [255,38,34],
    [255,102,0],
    [255,210,70],
    [34,112,255]
  ];
  const colorLut=Array.from({length:256},(_,index)=>{
    const scaled=index/255*4;
    const stop=Math.min(3,Math.floor(scaled));
    const mix=smoothstep(0,1,fract(scaled));
    return colorStops[stop].map((channel,channelIndex)=>
      Math.round(lerp(channel,colorStops[stop+1][channelIndex],mix))
    );
  });
  const colorAt=phase=>colorLut[Math.min(255,Math.floor(fract(phase)*256))];

  function seededRandom(seed){
    let state=seed>>>0;
    return()=>{
      state=(state*1664525+1013904223)>>>0;
      return state/4294967296;
    };
  }

  function quadraticPoint(start,control,end,progress){
    const inverse=1-progress;
    return{
      x:inverse*inverse*start.x+2*inverse*progress*control.x+progress*progress*end.x,
      y:inverse*inverse*start.y+2*inverse*progress*control.y+progress*progress*end.y
    };
  }

  function targetPoint(element){
    if(!(element instanceof Element))return{x:innerWidth/2,y:innerHeight*.78};
    const rect=element.getBoundingClientRect();
    let y=rect.top+rect.height/2;
    if(element.closest('.bdf-mobile-bottom')){
      const label=element.querySelector('span');
      const pseudo=getComputedStyle(element,'::before');
      const iconHeight=parseFloat(pseudo.height)||14;
      const style=getComputedStyle(element);
      const gap=parseFloat(style.rowGap||style.gap)||0;
      if(label)y=label.getBoundingClientRect().top-gap-iconHeight/2;
    }
    return{x:rect.left+rect.width/2,y};
  }

  function createFlash(point,reduced,strength=1){
    strength=clamp(Number(strength)||1,1,2.2);
    const flash=document.createElement('span');
    flash.className='bdf-particle-logo__flash';
    flash.setAttribute('aria-hidden','true');
    flash.style.setProperty('--bdf-particle-impact-x',`${point.x}px`);
    flash.style.setProperty('--bdf-particle-impact-y',`${point.y}px`);
    if(strength>1)flash.style.transformOrigin=`${point.x}px ${point.y}px`;
    document.body.appendChild(flash);
    const duration=reduced?260:Math.round(FLASH_MS*(1+(strength-1)*.28));
    const scale=1+(strength-1)*.34;
    const animation=flash.animate([
      {opacity:0,transform:strength>1?'scale(.9)':'none'},
      {opacity:reduced?.36:.98,transform:strength>1?`scale(${scale})`:'none',offset:.14},
      {opacity:reduced?.18:.62,transform:strength>1?`scale(${1+(strength-1)*.18})`:'none',offset:.40},
      {opacity:0,transform:strength>1?`scale(${1+(strength-1)*.08})`:'none'}
    ],{duration,easing:'cubic-bezier(.16,.76,.18,1)',fill:'forwards'});
    animation.finished.catch(()=>{}).finally(()=>flash.remove());
  }

  function loadMask(){
    if(maskPromise)return maskPromise;
    maskPromise=new Promise((resolve,reject)=>{
      const image=new Image();
      image.crossOrigin='anonymous';
      image.decoding='async';
      image.onload=()=>resolve(image);
      image.onerror=reject;
      image.src=IMAGE_URL;
    }).then(image=>{
      const side=640;
      const surface=document.createElement('canvas');
      surface.width=side;
      surface.height=side;
      const context=surface.getContext('2d',{willReadFrequently:true});
      context.clearRect(0,0,side,side);
      context.drawImage(image,0,0,side,side);
      const pixels=context.getImageData(0,0,side,side).data;
      const candidates=[];
      let minX=side,minY=side,maxX=-1,maxY=-1;
      for(let y=0;y<side;y+=2){
        for(let x=0;x<side;x+=2){
          const offset=(y*side+x)*4;
          const alpha=pixels[offset+3];
          if(alpha<28)continue;
          minX=Math.min(minX,x);
          minY=Math.min(minY,y);
          maxX=Math.max(maxX,x);
          maxY=Math.max(maxY,y);
          candidates.push({x,y,alpha:alpha/255});
        }
      }
      if(candidates.length<MAX_PARTICLES||maxX<=minX||maxY<=minY){
        throw new Error('BDF particle-logo mask is empty or undersampled.');
      }
      const random=seededRandom(0xBDF3013);
      for(let index=candidates.length-1;index>0;index-=1){
        const target=Math.floor(random()*(index+1));
        const value=candidates[index];
        candidates[index]=candidates[target];
        candidates[target]=value;
      }
      const span=maxX-minX;
      const centerX=(minX+maxX)/2;
      const centerY=(minY+maxY)/2;
      const points=[];
      for(let index=0;index<MAX_PARTICLES;index+=1){
        const candidate=candidates[Math.floor(index*candidates.length/MAX_PARTICLES)];
        points.push({
          nx:(candidate.x-centerX)/span,
          ny:(candidate.y-centerY)/span,
          nz:(random()-.5)*.28,
          alpha:.68+candidate.alpha*.32,
          size:.55+random()*1.55,
          phase:random()*TAU,
          phase2:random()*TAU,
          side:random()>.5?1:-1,
          burstAngle:random()*TAU,
          burstDepth:(random()-.5)*Math.PI,
          burstWeight:.62+random()*.92
        });
      }
      return{points};
    });
    return maskPromise;
  }

  class ParticleLogoController{
    constructor(main,mask,transientOnly=false){
      this.main=main;
      this.transientOnly=transientOnly;
      this.transientDone=false;
      this.restoreBackground=false;
      this.host=document.createElement('div');
      this.canvas=document.createElement('canvas');
      this.host.className='bdf-particle-logo';
      this.host.setAttribute('aria-hidden','true');
      this.host.dataset.bdfParticleLogo='1';
      this.host.dataset.bdfParticleLogoAsset=IMAGE_URL;
      this.host.dataset.bdfParticleLogoModel='single-canvas-stack-v1';
      this.host.dataset.bdfParticleLogoState='normal';
      this.host.appendChild(this.canvas);
      this.context=this.canvas.getContext('2d',{alpha:true,desynchronized:true});
      this.particles=[];
      this.buckets=Array.from({length:DEPTH_BUCKETS},()=>[]);
      this.pointer={x:0,y:0,targetX:0,targetY:0,strength:0,targetStrength:0,down:false};
      this.reaction={phase:'idle',startedAt:0,duration:760,startCenter:{x:0,y:0},startWidth:1,control:{x:0,y:0},target:null,targetPoint:{x:0,y:0},impactAt:0,impactPoint:{x:0,y:0},impactStrength:1,recoverAt:0,fallbackTimer:0};
      this.width=1;
      this.height=1;
      this.ratio=1;
      this.active=true;
      this.visible=true;
      this.raf=0;
      this.lastFrame=0;
      this.lastTime=0;
      this.motionTime=0;
      this.frameCount=0;
      this.lastCenter={x:innerWidth/2,y:innerHeight/2};
      this.lastLogoWidth=1;
      this.localScope={left:0,top:0,width:1,height:1};
      this.layer='';
      this.surface=null;
      this.observedRoot=null;
      this.observedSurface=null;
      this.stackReadyFrame=0;
      this.reduced=REDUCED_QUERY.matches;
      const random=seededRandom(0xBDF2026);
      mask.points.forEach(point=>this.particles.push({point,x:NaN,y:NaN,z:0,vx:0,vy:0,vz:0,glow:.70+random()*.30,frequency:.30+random()*.50,drawDepth:0,drawSize:1,drawAlpha:0,drawColor:0}));

      this.onResize=this.handleResize.bind(this);
      this.onModeChange=this.handleModeChange.bind(this);
      this.onVisibility=this.handleVisibility.bind(this);
      this.onPointerMove=this.handlePointerMove.bind(this);
      this.onPointerDown=this.handlePointerDown.bind(this);
      this.onPointerUp=this.handlePointerUp.bind(this);
      this.onFlightStart=this.handleFlightStart.bind(this);
      this.onFlightEnd=this.handleFlightEnd.bind(this);
      this.onPageShow=this.handlePageShow.bind(this);
      this.onPageHide=this.handlePageHide.bind(this);
      this.onSectionLoad=this.refresh.bind(this);
      this.loop=this.loop.bind(this);

      addEventListener('resize',this.onResize,{passive:true});
      window.visualViewport?.addEventListener('resize',this.onResize,{passive:true});
      MOBILE_QUERY.addEventListener?.('change',this.onModeChange);
      document.addEventListener('visibilitychange',this.onVisibility);
      document.addEventListener('pointermove',this.onPointerMove,{passive:true});
      document.addEventListener('pointerdown',this.onPointerDown,{passive:true});
      document.addEventListener('pointerup',this.onPointerUp,{passive:true});
      document.addEventListener('pointercancel',this.onPointerUp,{passive:true});
      document.addEventListener('pointerleave',this.onPointerUp,{passive:true});
      document.addEventListener('bdf:favorites:flightstart',this.onFlightStart);
      document.addEventListener('bdf:favorites:flightend',this.onFlightEnd);
      document.addEventListener('shopify:section:load',this.onSectionLoad);
      window.addEventListener('pageshow',this.onPageShow);
      window.addEventListener('pagehide',this.onPageHide);
      window.addEventListener('popstate',this.onPageShow);

      this.resizeObserver='ResizeObserver'in window?new ResizeObserver(()=>{
        if(!this.active||!this.isBackground())return;
        this.resize(false);
        this.resume(true);
      }):null;
      document.body.classList.add('bdf-particle-logo-enabled');
      if(this.transientOnly){
        this.mountViewport(false);
        this.host.style.visibility='hidden';
      }else{
        this.mountHomeBackground(true);
      }
      if(!this.transientOnly)this.raf=requestAnimationFrame(this.loop);
      this.host._bdfParticleLogoState=()=>this.stateSnapshot();
    }

    homeElements(){
      const root=document.querySelector(HOME_CAROUSEL_SELECTOR);
      return{
        root,
        stage:root?.querySelector(HOME_STAGE_SELECTOR)||null,
        mobile:root?.querySelector(HOME_MOBILE_SELECTOR)||null
      };
    }

    isBackground(){return this.layer==='home-stack'||this.layer==='home-mobile'}
    activeCount(){
      const transientFlight=this.transientOnly&&(this.reaction.phase==='forming'||this.reaction.phase==='flight');
      if(transientFlight)return MOBILE_QUERY.matches?220:360;
      return MOBILE_QUERY.matches?MOBILE_PARTICLES:DESKTOP_PARTICLES;
    }

    mountHomeBackground(resetParticles=false){
      if(this.transientOnly||!this.active)return false;
      const elements=this.homeElements();
      const target=MOBILE_QUERY.matches?elements.root:elements.stage;
      if(!elements.root||!target)return false;
      cancelAnimationFrame(this.stackReadyFrame);
      this.stackReadyFrame=0;
      target.appendChild(this.host);
      this.surface=target;
      this.layer=MOBILE_QUERY.matches?'home-mobile':'home-stack';
      this.host.dataset.bdfParticleLayer=this.layer;
      this.host.dataset.bdfParticleLogoState='normal';
      this.host.style.visibility=this.layer==='home-stack'?'hidden':'visible';
      if(this.observedRoot!==elements.root||this.observedSurface!==target){
        this.resizeObserver?.disconnect();
        this.resizeObserver?.observe(elements.root);
        if(target!==elements.root)this.resizeObserver?.observe(target);
        this.observedRoot=elements.root;
        this.observedSurface=target;
      }
      this.resize(resetParticles);
      if(this.layer==='home-stack')this.waitForStack();
      return true;
    }

    waitForStack(attempt=0){
      if(!this.active||this.layer!=='home-stack')return;
      const stack=this.surface?.style.getPropertyValue('--bdf-particle-stack-z')||getComputedStyle(this.surface).getPropertyValue('--bdf-particle-stack-z');
      if(stack.trim()){
        this.host.style.visibility='visible';
        this.host.dataset.bdfParticleStackReady='1';
        this.resume(true);
        return;
      }
      this.host.dataset.bdfParticleStackReady='0';
      if(attempt<120)this.stackReadyFrame=requestAnimationFrame(()=>this.waitForStack(attempt+1));
    }

    mountViewport(preserveScreen=true){
      if(!this.active)return;
      let screenPositions=null;
      let screenCenter=null;
      let screenWidth=this.lastLogoWidth;
      if(preserveScreen&&this.isBackground()&&this.width>1&&this.height>1){
        const rect=this.host.getBoundingClientRect();
        const map=point=>({
          x:rect.left+point.x/this.width*rect.width,
          y:rect.top+point.y/this.height*rect.height
        });
        screenPositions=this.particles.map(particle=>Number.isFinite(particle.x)?map(particle):null);
        screenCenter=map(this.lastCenter);
        screenWidth=this.lastLogoWidth*rect.width/this.width;
      }
      cancelAnimationFrame(this.stackReadyFrame);
      this.stackReadyFrame=0;
      document.body.insertBefore(this.host,this.main);
      this.surface=null;
      this.layer='flight';
      this.host.dataset.bdfParticleLayer='flight';
      this.host.style.visibility='visible';
      this.resizeObserver?.disconnect();
      this.observedRoot=null;
      this.observedSurface=null;
      this.resize(false);
      if(screenPositions){
        this.particles.forEach((particle,index)=>{
          const point=screenPositions[index];
          if(!point)return;
          particle.x=point.x;
          particle.y=point.y;
          particle.vx=0;
          particle.vy=0;
          particle.vz=0;
        });
      }
      if(screenCenter)this.lastCenter=screenCenter;
      this.lastLogoWidth=screenWidth;
    }

    computeLocalScope(){
      if(this.layer==='home-stack')return{left:0,top:0,width:this.width,height:this.height};
      if(this.layer==='home-mobile'){
        const elements=this.homeElements();
        const rootRect=elements.root?.getBoundingClientRect();
        const focus=elements.mobile?.querySelector('.slick-list')||elements.mobile||elements.root;
        const focusRect=focus?.getBoundingClientRect();
        if(rootRect&&focusRect&&focusRect.width>0&&focusRect.height>0){
          return{
            left:focusRect.left-rootRect.left,
            top:focusRect.top-rootRect.top,
            width:focusRect.width,
            height:focusRect.height
          };
        }
      }
      return{left:0,top:0,width:this.width,height:this.height};
    }

    resize(resetParticles=false){
      const oldWidth=this.width;
      const oldHeight=this.height;
      if(this.isBackground()&&this.surface){
        this.width=Math.max(1,Math.round(this.surface.clientWidth||this.surface.getBoundingClientRect().width||1));
        this.height=Math.max(1,Math.round(this.surface.clientHeight||this.surface.getBoundingClientRect().height||1));
      }else{
        this.width=Math.max(1,Math.round(window.visualViewport?.width||innerWidth||1));
        this.height=Math.max(1,Math.round(window.visualViewport?.height||innerHeight||1));
      }
      this.ratio=this.layer==='home-stack'?Math.min(3.6,Math.max(2.8,parseFloat(getComputedStyle(this.surface).getPropertyValue('--bdf-particle-home-scale'))||3.6)):Math.max(1,Math.min(MOBILE_QUERY.matches?1.15:1.35,devicePixelRatio||1));
      const pixelWidth=Math.max(2,Math.round(this.width*this.ratio));
      const pixelHeight=Math.max(2,Math.round(this.height*this.ratio));
      if(this.canvas.width!==pixelWidth||this.canvas.height!==pixelHeight){
        this.canvas.width=pixelWidth;
        this.canvas.height=pixelHeight;
      }
      this.host.style.width=`${this.width}px`;
      this.host.style.height=`${this.height}px`;
      this.host.dataset.bdfParticleLogoResolution=`${pixelWidth}x${pixelHeight}`;
      this.localScope=this.computeLocalScope();
      const substantialResize=Math.abs(oldWidth-this.width)>2||Math.abs(oldHeight-this.height)>2;
      if(resetParticles||substantialResize){
        this.particles.forEach(particle=>{
          particle.x=NaN;
          particle.y=NaN;
          particle.z=0;
          particle.vx=0;
          particle.vy=0;
          particle.vz=0;
        });
      }
    }

    currentMode(){
      if(this.layer==='home-stack')return'desktop-home-stack';
      if(this.layer==='home-mobile')return'mobile-home-background';
      return MOBILE_QUERY.matches?'mobile-flight':'desktop-flight';
    }

    stateSnapshot(){
      const elements=this.homeElements();
      return{
        version:VERSION,
        model:this.host.dataset.bdfParticleLogoModel,
        asset:IMAGE_URL,
        mode:this.currentMode(),
        layer:this.layer,
        count:this.activeCount(),
        frames:this.frameCount,
        state:this.host.dataset.bdfParticleLogoState,
        visible:this.visible,
        reaction:this.reaction.phase,
        impactStrength:this.reaction.impactStrength,
        resolution:this.host.dataset.bdfParticleLogoResolution,
        center:{...this.lastCenter},
        logoWidth:this.lastLogoWidth,
        parent:this.host.parentElement===elements.stage?'stage':this.host.parentElement===elements.root?'carousel':'viewport',
        stack:this.layer==='home-stack'?parseFloat(getComputedStyle(this.host).zIndex):null,
        canvasCount:this.host.querySelectorAll('canvas').length,
        totalHosts:document.querySelectorAll('[data-bdf-particle-logo]').length,
        legacyOverlays:document.querySelectorAll('.bdf-particle-logo-rear-overlay').length
      };
    }

    backgroundVisible(){
      const root=this.homeElements().root;
      if(!root)return false;
      const rect=root.getBoundingClientRect();
      return rect.bottom>0&&rect.top<innerHeight&&rect.right>0&&rect.left<innerWidth;
    }

    handleResize(){
      if(!this.active)return;
      if(this.isBackground())this.mountHomeBackground(false);
      else this.resize(false);
      this.resume(true);
    }

    handleModeChange(){
      if(!this.active||this.transientOnly)return;
      if(this.reaction.phase==='idle')this.mountHomeBackground(true);
    }

    handleVisibility(){
      if(document.hidden){
        this.host.dataset.bdfParticleLogoState='paused';
        this.lastTime=0;
        if(this.raf)cancelAnimationFrame(this.raf);
        this.raf=0;
      }else{
        this.resume(true);
      }
    }

    handlePageShow(){this.refresh();this.resume(true)}
    handlePageHide(event){
      if(event.persisted){
        this.lastTime=0;
        if(this.raf)cancelAnimationFrame(this.raf);
        this.raf=0;
      }
    }

    refresh(){
      if(!this.active)return;
      const main=document.querySelector(MAIN_SELECTOR);
      if(main)this.main=main;
      if(!this.transientOnly&&this.reaction.phase==='idle')this.mountHomeBackground(false);
      else this.resize(false);
    }

    resume(forcePaint=false){
      if(!this.active)return;
      this.lastTime=0;
      this.visible=this.isBackground()?this.backgroundVisible():true;
      if(forcePaint&&this.visible)this.paint(performance.now(),true);
      if(!this.reduced&&!document.hidden&&!this.raf)this.raf=requestAnimationFrame(this.loop);
    }

    pointerToLocal(event){
      if(!this.isBackground())return{x:event.clientX,y:event.clientY};
      const rect=this.host.getBoundingClientRect();
      return{
        x:(event.clientX-rect.left)*this.width/Math.max(1,rect.width),
        y:(event.clientY-rect.top)*this.height/Math.max(1,rect.height)
      };
    }

    handlePointerMove(event){
      if(this.reduced||!this.active||!this.isBackground())return;
      if(event.pointerType==='touch'&&!this.pointer.down)return;
      const root=this.homeElements().root;
      const rect=root?.getBoundingClientRect();
      const local=this.pointerToLocal(event);
      const inside=Boolean(rect&&event.clientX>=rect.left&&event.clientX<=rect.right&&event.clientY>=rect.top&&event.clientY<=rect.bottom);
      this.pointer.targetX=local.x;
      this.pointer.targetY=local.y;
      if(!this.pointer.strength){this.pointer.x=local.x;this.pointer.y=local.y}
      this.pointer.targetStrength=inside?1:0;
    }

    handlePointerDown(event){this.pointer.down=true;this.handlePointerMove(event)}
    handlePointerUp(){this.pointer.down=false;this.pointer.targetStrength=0}

    handleFlightStart(event){
      const detail=event.detail||{};
      if(!(detail.source instanceof Element)||!(detail.target instanceof Element))return;
      const now=performance.now();
      if(!this.transientOnly&&this.isBackground())this.mountViewport(true);
      const source=detail.source;
      const transientStart=this.transientOnly?targetPoint(source):null;
      const startCenter=transientStart||{...this.lastCenter};
      const startWidth=this.transientOnly?(MOBILE_QUERY.matches?96:130):Math.max(1,this.lastLogoWidth);
      if(this.transientOnly){
        this.lastCenter={...startCenter};
        this.lastLogoWidth=startWidth;
        this.particles.forEach(particle=>{
          particle.x=NaN;
          particle.y=NaN;
          particle.z=0;
          particle.vx=0;
          particle.vy=0;
          particle.vz=0;
        });
      }
      const target=detail.target;
      const end=detail.end&&Number.isFinite(detail.end.x)&&Number.isFinite(detail.end.y)?{x:detail.end.x,y:detail.end.y}:targetPoint(target);
      const distance=Math.hypot(end.x-startCenter.x,end.y-startCenter.y);
      const intendedStartedAt=Number.isFinite(detail.startedAt)?detail.startedAt:now;
      this.reaction.phase='forming';
      this.reaction.startedAt=this.transientOnly?now:intendedStartedAt;
      this.reaction.duration=clamp(Number(detail.duration)||760,520,1600);
      this.reaction.startCenter=startCenter;
      this.reaction.startWidth=startWidth;
      this.reaction.target=target;
      this.reaction.targetPoint=end;
      this.reaction.impactStrength=clamp(Number(detail.impactStrength)||1,1,2.2);
      this.reaction.control={x:(startCenter.x+end.x)/2,y:Math.min(startCenter.y,end.y)-Math.max(90,Math.min(260,distance*.28))};
      this.reaction.impactAt=0;
      this.reaction.recoverAt=0;
      clearTimeout(this.reaction.fallbackTimer);
      this.reaction.fallbackTimer=setTimeout(()=>{
        if(this.reaction.phase==='forming'||this.reaction.phase==='flight')this.beginImpact(targetPoint(this.reaction.target),this.reaction.impactStrength);
      },this.reaction.duration+900);
      this.host.style.visibility='visible';
      this.host.dataset.bdfParticleLogoState='flight';
      this.resume(true);
      if(this.transientOnly)this.reaction.startedAt=intendedStartedAt;
    }

    handleFlightEnd(event){
      if(this.reaction.phase!=='forming'&&this.reaction.phase!=='flight')return;
      const detail=event.detail||{};
      const exact=detail.point&&Number.isFinite(detail.point.x)&&Number.isFinite(detail.point.y)?{x:detail.point.x,y:detail.point.y}:targetPoint(detail.target||this.reaction.target);
      this.beginImpact(exact,detail.impactStrength||this.reaction.impactStrength);
    }

    beginImpact(point,strength=1){
      clearTimeout(this.reaction.fallbackTimer);
      this.reaction.fallbackTimer=0;
      this.reaction.phase='impact';
      this.reaction.impactAt=performance.now();
      this.reaction.impactPoint=point;
      this.reaction.targetPoint=point;
      this.reaction.impactStrength=clamp(Number(strength)||1,1,2.2);
      for(const particle of this.particles){
        particle.x=point.x;
        particle.y=point.y;
        particle.z=0;
        particle.vx=0;
        particle.vy=0;
        particle.vz=0;
      }
      this.host.dataset.bdfParticleLogoState='impact';
      createFlash(point,this.reduced,this.reaction.impactStrength);
      this.resume(true);
    }

    updatePointer(deltaTime){
      const positionBlend=1-Math.exp(-deltaTime*14);
      const strengthBlend=1-Math.exp(-deltaTime*(this.pointer.targetStrength>this.pointer.strength?10:5.5));
      this.pointer.x=lerp(this.pointer.x,this.pointer.targetX,positionBlend);
      this.pointer.y=lerp(this.pointer.y,this.pointer.targetY,positionBlend);
      this.pointer.strength=lerp(this.pointer.strength,this.pointer.targetStrength,strengthBlend);
      if(this.pointer.strength<.001)this.pointer.strength=0;
      this.host.dataset.bdfParticleLogoPointer=this.pointer.strength>.04?'active':'idle';
    }

    baseState(time){
      const heartbeatPhase=fract(time/2.86);
      const stage=time*4/9.2;
      const stagePhase=fract(stage);
      const transition=smoothstep(.69,.98,stagePhase);
      return{
        pulse:clamp(circularGaussian(heartbeatPhase,.105,.044)+.66*circularGaussian(heartbeatPhase,.225,.068),0,1),
        breath:.5+.5*Math.sin(time*TAU/6.2-.72),
        colorPhase:(Math.floor(stage)+transition)/4,
        compression:Math.exp(-Math.pow((stagePhase-.73)/.12,2)),
        release:circularGaussian(stagePhase,.018,.095)
      };
    }

    reactionState(now){
      const reaction=this.reaction;
      if(reaction.phase==='forming'||reaction.phase==='flight'){
        const raw=clamp((now-reaction.startedAt)/reaction.duration,0,1);
        const form=smoothstep(0,.22,raw);
        const travel=smoothstep(.18,.985,raw);
        reaction.phase=raw<.22?'forming':'flight';
        const dynamicEnd=reaction.target instanceof Element?targetPoint(reaction.target):reaction.targetPoint;
        reaction.targetPoint=dynamicEnd;
        const distance=Math.hypot(dynamicEnd.x-reaction.startCenter.x,dynamicEnd.y-reaction.startCenter.y);
        reaction.control={x:(reaction.startCenter.x+dynamicEnd.x)/2,y:Math.min(reaction.startCenter.y,dynamicEnd.y)-Math.max(90,Math.min(260,distance*.28))};
        return{phase:reaction.phase,raw,form,travel,center:quadraticPoint(reaction.startCenter,reaction.control,dynamicEnd,travel),impact:0,impactProgress:0,opacity:1};
      }
      if(reaction.phase==='impact'){
        const impactDuration=IMPACT_MS*(1+(reaction.impactStrength-1)*.24);
        const progress=clamp((now-reaction.impactAt)/impactDuration,0,1);
        if(progress>=1){
          if(this.transientOnly){
            this.transientDone=true;
            return{phase:'impact',raw:1,form:1,travel:1,center:reaction.impactPoint,impact:0,impactProgress:1,opacity:0};
          }
          reaction.phase='recover';
          reaction.recoverAt=now;
          this.host.dataset.bdfParticleLogoState='recover';
          return{phase:'recover',raw:1,form:0,travel:0,center:reaction.impactPoint,impact:0,impactProgress:1,opacity:1};
        }
        const attack=1-Math.exp(-progress*22);
        return{phase:'impact',raw:1,form:1,travel:1,center:reaction.impactPoint,impact:attack*Math.pow(1-progress,.62),impactProgress:progress,opacity:1};
      }
      if(reaction.phase==='recover'){
        const progress=clamp((now-reaction.recoverAt)/RECOVER_MS,0,1);
        if(progress>=1){
          reaction.phase='idle';
          this.restoreBackground=true;
          return{phase:'recover',raw:1,form:0,travel:0,center:reaction.impactPoint,impact:0,impactProgress:1,opacity:0};
        }
        return{phase:'recover',raw:1,form:0,travel:0,center:reaction.impactPoint,impact:0,impactProgress:1,opacity:1-smoothstep(0,1,progress)};
      }
      return{phase:'idle',raw:0,form:0,travel:0,center:null,impact:0,impactProgress:1,opacity:1};
    }

    geometry(time,base,reactionState){
      const mobile=MOBILE_QUERY.matches;
      const background=this.isBackground();
      const scope=background?this.localScope:{left:0,top:0,width:this.width,height:this.height};
      const homeDesktop=this.layer==='home-stack';
      const homeMobile=this.layer==='home-mobile';
      const baseWidth=homeDesktop?
        Math.min(1080,scope.width*.56,scope.height*1.11):
        homeMobile?
          Math.min(410,scope.width*.92,scope.height*.86):
          mobile?
            Math.min(410,this.width*.92,this.height*.50):
            Math.min(720,this.width*.56,this.height*.68);
      const anchor={x:scope.left+scope.width/2,y:scope.top+scope.height/2};
      const driftX=Math.sin(time*.23+1.1)*baseWidth*.035+Math.sin(time*.11)*baseWidth*.018;
      const driftY=Math.cos(time*.19+.4)*baseWidth*.026+Math.sin(time*.13)*baseWidth*.014;
      let center={x:anchor.x+driftX,y:anchor.y+driftY};
      let logoWidth=baseWidth*(1-base.compression*.14)*(1+base.release*.19)*(1+base.pulse*.035);
      let foreground=false;
      if(reactionState.phase==='forming'||reactionState.phase==='flight'){
        foreground=true;
        center=reactionState.center;
        const flightWidth=this.transientOnly?(mobile?72:96):(mobile?46:42);
        logoWidth=lerp(this.reaction.startWidth*(1+.08*reactionState.form),flightWidth,smoothstep(.18,1,reactionState.raw));
      }else if(reactionState.phase==='impact'||reactionState.phase==='recover'){
        foreground=true;
        center=reactionState.center;
        logoWidth=(mobile?46:42)*(1+(this.reaction.impactStrength-1)*.18);
      }
      this.lastCenter=center;
      this.lastLogoWidth=logoWidth;
      return{
        mobile,
        background,
        homeDesktop,
        homeMobile,
        foreground,
        center,
        logoWidth,
        depth:logoWidth*(mobile?.23:.28),
        yaw:Math.sin(time*.27)*.26,
        pitch:Math.sin(time*.21+.7)*.19,
        roll:Math.sin(time*.17)*.075,
        reaction:reactionState
      };
    }

    updateParticles(time,deltaTime,base,geometry){
      const count=this.activeCount();
      const reaction=geometry.reaction;
      const pointerRadius=Math.min(this.width,this.height)*(geometry.mobile?.30:.18);
      const foreground=geometry.foreground;
      const transientFlight=this.transientOnly&&(reaction.phase==='forming'||reaction.phase==='flight');
      const cosYaw=Math.cos(geometry.yaw),sinYaw=Math.sin(geometry.yaw);
      const cosPitch=Math.cos(geometry.pitch),sinPitch=Math.sin(geometry.pitch);
      const cosRoll=Math.cos(geometry.roll),sinRoll=Math.sin(geometry.roll);
      this.buckets.forEach(bucket=>{bucket.length=0});
      for(let index=0;index<count;index+=1){
        const particle=this.particles[index];
        const point=particle.point;
        const localX=point.nx*geometry.logoWidth+Math.sin(time*particle.frequency+point.phase)*geometry.logoWidth*.0055;
        const localY=point.ny*geometry.logoWidth+Math.cos(time*particle.frequency*.82+point.phase2)*geometry.logoWidth*.0055;
        const localZ=point.nz*geometry.depth+Math.sin(time*.37+point.phase)*geometry.depth*.11;
        let rotatedX=localX*cosYaw+localZ*sinYaw;
        let rotatedZ=-localX*sinYaw+localZ*cosYaw;
        let rotatedY=localY*cosPitch-rotatedZ*sinPitch;
        rotatedZ=localY*sinPitch+rotatedZ*cosPitch;
        const finalX=rotatedX*cosRoll-rotatedY*sinRoll;
        const finalY=rotatedX*sinRoll+rotatedY*cosRoll;
        const perspective=clamp(1/(1-rotatedZ/Math.max(1,geometry.logoWidth*1.85)),.72,1.38);
        let targetX=geometry.center.x+finalX*perspective;
        let targetY=geometry.center.y+finalY*perspective;
        let targetZ=rotatedZ;
        if(reaction.phase==='impact'){
          const radial=Math.cos(point.burstDepth);
          const impactStrength=this.reaction.impactStrength;
          const radius=geometry.logoWidth*(1.2+9.2*point.burstWeight)*reaction.impact*impactStrength;
          const fadeMix=Math.pow(1-reaction.impactProgress,.48);
          targetX=lerp(targetX,reaction.center.x+Math.cos(point.burstAngle)*radial*radius,fadeMix);
          targetY=lerp(targetY,reaction.center.y+Math.sin(point.burstAngle)*radial*radius,fadeMix);
          targetZ=lerp(targetZ,Math.sin(point.burstDepth)*geometry.depth*8*reaction.impact*impactStrength,fadeMix);
        }
        if(this.pointer.strength>0&&!foreground){
          let dx=targetX-this.pointer.x;
          let dy=targetY-this.pointer.y;
          let distance=Math.hypot(dx,dy);
          if(distance<pointerRadius){
            if(distance<.01){dx=Math.cos(point.phase);dy=Math.sin(point.phase);distance=1}
            const force=Math.pow(smoothstep(0,1,1-distance/pointerRadius),1.18)*this.pointer.strength;
            const normalX=dx/distance;
            const normalY=dy/distance;
            const push=pointerRadius*.78*force;
            const swirl=pointerRadius*.28*force*point.side;
            targetX+=normalX*push-normalY*swirl;
            targetY+=normalY*push+normalX*swirl;
            targetZ+=point.side*geometry.depth*.20*force;
          }
        }
        if(transientFlight){
          particle.x=targetX;
          particle.y=targetY;
          particle.z=targetZ;
          particle.vx=0;
          particle.vy=0;
          particle.vz=0;
        }else{
          if(!Number.isFinite(particle.x)){
            particle.x=targetX;
            particle.y=targetY;
            particle.z=targetZ;
          }
          const stiffness=foreground?44:18;
          const damping=foreground?10.8:7.5;
          particle.vx+=(targetX-particle.x)*stiffness*deltaTime;
          particle.vy+=(targetY-particle.y)*stiffness*deltaTime;
          particle.vz+=(targetZ-particle.z)*stiffness*deltaTime;
          const decay=Math.exp(-damping*deltaTime);
          particle.vx*=decay;
          particle.vy*=decay;
          particle.vz*=decay;
          particle.x+=particle.vx*deltaTime;
          particle.y+=particle.vy*deltaTime;
          particle.z+=particle.vz*deltaTime;
        }
        particle.drawDepth=clamp(.5+particle.z/Math.max(1,geometry.depth*1.7),0,1);
        particle.drawSize=(.58+point.size*1.28)*(.78+particle.drawDepth*.50)*(1+reaction.impact*.34*(1+(this.reaction.impactStrength-1)*.45));
        particle.drawAlpha=point.alpha*particle.glow*(.43+particle.drawDepth*.57)*(1+base.pulse*.16+reaction.impact*.22*(1+(this.reaction.impactStrength-1)*.5))*reaction.opacity;
        particle.drawColor=base.colorPhase+(particle.drawDepth-.5)*.055+index/count*.018;
        this.buckets[Math.min(DEPTH_BUCKETS-1,Math.floor(particle.drawDepth*DEPTH_BUCKETS))].push(particle);
      }
    }

    render(base,geometry){
      const context=this.context;
      const desktopDust=geometry.homeDesktop&&!geometry.foreground;
      const desktopScale=desktopDust?(parseFloat(getComputedStyle(this.surface).getPropertyValue('--bdf-particle-home-scale'))||1):1;
      const mainColor=colorAt(base.colorPhase);
      const opacity=geometry.reaction.opacity;
      const transientFlight=this.transientOnly&&(geometry.reaction.phase==='forming'||geometry.reaction.phase==='flight');
      if(!desktopDust){
        const haloRadius=geometry.logoWidth*(transientFlight?.46:(.62+base.breath*.10+base.pulse*.08)*(1-base.compression*.08)*(1+base.release*.12));
        const halo=context.createRadialGradient(geometry.center.x,geometry.center.y,0,geometry.center.x,geometry.center.y,haloRadius);
        if(transientFlight){
          halo.addColorStop(0,rgba(mainColor,.026*opacity));
          halo.addColorStop(.42,rgba(mainColor,.010*opacity));
        }else{
          halo.addColorStop(0,rgba(mainColor,(.13+base.pulse*.045)*opacity));
          halo.addColorStop(.36,rgba(mainColor,(.058+base.breath*.020)*opacity));
        }
        halo.addColorStop(1,rgba(mainColor,0));
        context.fillStyle=halo;
        context.fillRect(0,0,this.width,this.height);
      }
      if(geometry.reaction.phase==='impact'){
        const impactProgress=geometry.reaction.impactProgress;
        const strength=this.reaction.impactStrength;
        const impactRadius=Math.min(this.width,this.height)*(.08+smoothstep(0,.72,impactProgress)*.72)*(1+(strength-1)*.55);
        const impactFade=Math.pow(1-impactProgress,.82);
        const impact=context.createRadialGradient(geometry.center.x,geometry.center.y,0,geometry.center.x,geometry.center.y,impactRadius);
        impact.addColorStop(0,`rgba(255,255,255,${clamp(impactFade*(.94+(strength-1)*.06),0,1).toFixed(3)})`);
        impact.addColorStop(.18,`rgba(255,238,150,${clamp(impactFade*(.82+(strength-1)*.10),0,1).toFixed(3)})`);
        impact.addColorStop(.48,rgba(colorAt(base.colorPhase+.52),impactFade*(.38+(strength-1)*.14)));
        impact.addColorStop(1,'rgba(0,0,0,0)');
        context.fillStyle=impact;
        context.beginPath();
        context.arc(geometry.center.x,geometry.center.y,impactRadius,0,TAU);
        context.fill();
      }
      for(const bucket of this.buckets){
        for(let index=0;index<bucket.length;index+=1){
          const particle=bucket[index];
          if(particle.x<-40||particle.x>this.width+40||particle.y<-40||particle.y>this.height+40)continue;
          const color=colorAt(particle.drawColor);
          const alpha=clamp(particle.drawAlpha,0,1);
          if(desktopDust){
            const radius=Math.max(.08,particle.drawSize*.58/desktopScale);
            context.fillStyle=rgba(color,alpha*.14);
            context.beginPath();
            context.arc(particle.x,particle.y,radius*(3.25+base.pulse*.30),0,TAU);
            context.fill();
            context.fillStyle=rgba(color,alpha*.88);
            context.beginPath();
            context.arc(particle.x,particle.y,radius,0,TAU);
            context.fill();
            if(index%3===0){
              context.fillStyle=`rgba(255,255,255,${(alpha*(.25+particle.drawDepth*.30)).toFixed(3)})`;
              context.beginPath();
              context.arc(particle.x-radius*.16,particle.y-radius*.18,Math.max(.06,radius*.30),0,TAU);
              context.fill();
            }
          }else{
            const radius=Math.max(.52,particle.drawSize*.58);
            context.fillStyle=rgba(color,alpha*(transientFlight?.045:.14));
            context.beginPath();
            context.arc(particle.x,particle.y,radius*(transientFlight?2.2:(3.25+base.pulse*.30)),0,TAU);
            context.fill();
            context.fillStyle=rgba(color,alpha*(transientFlight?.84:.88));
            context.beginPath();
            context.arc(particle.x,particle.y,radius,0,TAU);
            context.fill();
            if(!transientFlight&&index%3===0){
              context.fillStyle=`rgba(255,255,255,${(alpha*(.25+particle.drawDepth*.30)).toFixed(3)})`;
              context.beginPath();
              context.arc(particle.x-radius*.16,particle.y-radius*.18,Math.max(.26,radius*.30),0,TAU);
              context.fill();
            }
          }
        }
      }
    }

    paint(now,force=false){
      if(!this.active||!this.context)return;
      this.visible=this.isBackground()?this.backgroundVisible():true;
      if(document.hidden||!this.visible){
        this.host.dataset.bdfParticleLogoState='paused';
        this.lastTime=0;
        return;
      }
      if(this.reduced&&this.frameCount>0&&!force)return;
      if(!force&&now-this.lastFrame<FRAME_MS-1)return;
      const deltaTime=this.reduced?0:clamp(this.lastTime?(now-this.lastTime)/1000:1/60,1/240,1/28);
      this.lastTime=now;
      this.lastFrame=now;
      this.updatePointer(deltaTime);
      const reactionState=this.reactionState(now);
      let base=this.baseState(this.motionTime);
      const speed=clamp(.78+base.pulse*.18-base.compression*.18+base.release*.42+reactionState.impact*.34,.55,1.56);
      this.motionTime+=deltaTime*speed;
      base=this.baseState(this.motionTime);
      const geometry=this.geometry(this.motionTime,base,reactionState);
      this.updateParticles(this.motionTime,deltaTime,base,geometry);
      this.context.setTransform(1,0,0,1,0,0);
      this.context.clearRect(0,0,this.canvas.width,this.canvas.height);
      this.context.setTransform(this.ratio,0,0,this.ratio,0,0);
      this.context.save();
      const transientFlight=this.transientOnly&&(reactionState.phase==='forming'||reactionState.phase==='flight');
      this.context.globalCompositeOperation=transientFlight?'source-over':'lighter';
      this.render(base,geometry);
      this.context.restore();

      if(this.transientDone){
        this.destroy();
        if(transientController===this)transientController=null;
        return;
      }
      if(this.restoreBackground){
        this.restoreBackground=false;
        this.mountHomeBackground(true);
        return;
      }
      this.frameCount+=1;
      this.host.dataset.bdfParticleLogoFrames=String(this.frameCount);
      this.host.dataset.bdfParticleLogoCount=String(this.activeCount());
      this.host.dataset.bdfParticleLogoMode=this.currentMode();
      if(this.reaction.phase==='idle')this.host.dataset.bdfParticleLogoState=this.reduced?'static':'normal';
    }

    loop(now){
      this.raf=0;
      this.paint(now);
      if(this.active&&!this.reduced&&!document.hidden)this.raf=requestAnimationFrame(this.loop);
    }

    destroy(){
      this.active=false;
      cancelAnimationFrame(this.stackReadyFrame);
      if(this.raf)cancelAnimationFrame(this.raf);
      clearTimeout(this.reaction.fallbackTimer);
      this.resizeObserver?.disconnect();
      removeEventListener('resize',this.onResize);
      window.visualViewport?.removeEventListener('resize',this.onResize);
      MOBILE_QUERY.removeEventListener?.('change',this.onModeChange);
      document.removeEventListener('visibilitychange',this.onVisibility);
      document.removeEventListener('pointermove',this.onPointerMove);
      document.removeEventListener('pointerdown',this.onPointerDown);
      document.removeEventListener('pointerup',this.onPointerUp);
      document.removeEventListener('pointercancel',this.onPointerUp);
      document.removeEventListener('pointerleave',this.onPointerUp);
      document.removeEventListener('bdf:favorites:flightstart',this.onFlightStart);
      document.removeEventListener('bdf:favorites:flightend',this.onFlightEnd);
      document.removeEventListener('shopify:section:load',this.onSectionLoad);
      window.removeEventListener('pageshow',this.onPageShow);
      window.removeEventListener('pagehide',this.onPageHide);
      window.removeEventListener('popstate',this.onPageShow);
      delete this.host._bdfParticleLogoState;
      this.host.remove();
      if(!document.querySelector('[data-bdf-particle-logo]'))document.body.classList.remove('bdf-particle-logo-enabled');
    }
  }

  async function boot(){
    if(controller){controller.resume(true);return controller}
    if(bootPromise)return bootPromise;
    bootPromise=(async()=>{
      const main=document.querySelector(MAIN_SELECTOR);
      if(!main)return null;
      const mask=await loadMask();
      controller=new ParticleLogoController(main,mask,false);
      window.BDFParticleLogo={
        version:VERSION,
        asset:IMAGE_URL,
        state:()=>controller?.stateSnapshot()||null,
        refresh:()=>controller?.refresh(),
        resume:()=>controller?.resume(true),
        destroy:()=>{controller?.destroy();controller=null}
      };
      return controller;
    })().catch(error=>{
      console.error('[BDF_PARTICLE_LOGO]',error);
      return null;
    }).finally(()=>{bootPromise=null});
    return bootPromise;
  }

  function setupTransientMode(){
    const ready=loadMask();
    window.BDFParticleLogo={
      version:VERSION,
      asset:IMAGE_URL,
      ready,
      state:()=>transientController?.stateSnapshot()||null,
      destroy:()=>{transientController?.destroy();transientController=null}
    };
    document.addEventListener('bdf:favorites:flightstart',async event=>{
      const detail=event.detail||{};
      if(!(detail.source instanceof Element)||!(detail.target instanceof Element))return;
      const main=document.querySelector(MAIN_SELECTOR);
      if(!main)return;
      try{
        const mask=await ready;
        transientController?.destroy();
        transientController=new ParticleLogoController(main,mask,true);
        transientController.paint(performance.now(),true);
        transientController.handleFlightStart(event);
        transientController.host.style.visibility='visible';
      }catch(error){
        console.error('[BDF_PARTICLE_LOGO_TRANSIENT]',error);
      }
    });
  }

  async function triggerExternalImpact(detail={}){
    const main=document.querySelector(MAIN_SELECTOR);
    if(!main)return;
    const supplied=detail.point;
    const point=supplied&&Number.isFinite(supplied.x)&&Number.isFinite(supplied.y)?{x:supplied.x,y:supplied.y}:targetPoint(detail.target);
    const strength=clamp(Number(detail.impactStrength)||1,1,2.2);
    if(REDUCED_QUERY.matches){createFlash(point,true,strength);return;}
    try{
      const mask=await loadMask();
      transientController?.destroy();
      transientController=new ParticleLogoController(main,mask,true);
      transientController.beginImpact(point,strength);
      transientController.host.style.visibility='visible';
    }catch(error){
      console.error('[BDF_PARTICLE_IMPACT]',error);
    }
  }

  document.addEventListener('bdf:particles:impact',event=>triggerExternalImpact(event.detail||{}));

  function ensureRunning(){if(controller)controller.resume(true);else boot()}
  if(document.body.classList.contains('template-index')){
    if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',boot,{once:true});
    else boot();
    window.addEventListener('pageshow',ensureRunning);
    document.addEventListener('shopify:section:load',ensureRunning);
  }else{
    setupTransientMode();
  }
})();
